import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';

const Homepage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const examplePrompts = [
    'Summarize a PDF',
    'Create a presentation',
    'Edit a video',
    'Do research on a topic',
    'Automate emails'
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/results?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleExampleClick = (example) => {
    setSearchQuery(example);
    navigate(`/results?q=${encodeURIComponent(example)}`);
  };

  return (
    <Layout>
      <div className="py-20 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-black"
              data-testid="homepage-hero-title"
            >
              Find the Right AI Tool
            </h1>
            
            <p 
              className="text-xl md:text-2xl leading-relaxed text-[#333333]"
              data-testid="homepage-subtext"
            >
              Simply tell us what you want to do.
            </p>

            <form onSubmit={handleSearch} className="mt-12">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="What do you want to do?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 px-6 pr-14 text-lg bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl w-full transition-all placeholder:text-gray-500"
                  data-testid="homepage-search-input"
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-10 w-10 rounded-full p-0"
                  data-testid="homepage-search-button"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </form>

            <div className="mt-8">
              <p className="text-sm font-medium text-[#333333] mb-4">Or try one of these:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(prompt)}
                    className="px-6 py-2 bg-white border-2 border-gray-200 text-black rounded-full text-base font-medium hover:border-[#0044CC] hover:bg-[#0044CC]/5 transition-colors focus-visible-ring"
                    data-testid={`example-prompt-${index}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-black text-center mb-16">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#0044CC] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto">
                1
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-black">Tell us your goal</h3>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                Describe what you want to accomplish in simple terms.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#0044CC] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto">
                2
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-black">Get clear recommendations</h3>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                We show you the top 3-5 tools that match your needs.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#0044CC] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto">
                3
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-black">Choose with confidence</h3>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                Each recommendation includes a clear explanation of why it fits.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="py-20">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-black mb-8">
            Explore AI Tools by Category
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
            {['Writing', 'Image & Design', 'Video & Audio', 'Coding', 'Research', 'Productivity', 'Automation'].map((category) => (
              <button
                key={category}
                onClick={() => navigate(`/results?category=${encodeURIComponent(category)}`)}
                className="p-6 bg-white border-2 border-gray-100 rounded-2xl text-center hover:border-[#0044CC]/50 transition-colors focus-visible-ring"
                data-testid={`category-${category.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`}
              >
                <h3 className="text-xl font-semibold text-black">{category}</h3>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Homepage;